import React, { useState, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useDropzone } from "react-dropzone";
import { useContextToast } from "../../contexts/toast";

interface UploadModalProps {
  show: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
  isUploading?: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const MAX_FILES = 20;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const FilePreview: React.FC<{ file: File; onRemove: () => void }> = ({
  file,
  onRemove,
}) => {
  const [preview, setPreview] = useState<string | null>(null);

  React.useEffect(() => {
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  const getFileIcon = () => {
    if (file.type.startsWith("image/")) {
      return preview ? (
        <img
          src={preview}
          alt={file.name}
          style={{ width: "40px", height: "40px", objectFit: "cover" }}
        />
      ) : (
        <div className="nk-upload-icon">
          <em className="icon ni ni-img"></em>
        </div>
      );
    }

    return (
      <div className="nk-upload-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 72 72"
          style={{ width: "40px", height: "40px" }}
        >
          <g>
            <rect
              x="16"
              y="14"
              width="40"
              height="44"
              rx="6"
              ry="6"
              style={{ fill: "#7e95c4" }}
            ></rect>
            <rect
              x="32"
              y="17"
              width="8"
              height="2"
              rx="1"
              ry="1"
              style={{ fill: "#fff" }}
            ></rect>
            <rect
              x="32"
              y="22"
              width="8"
              height="2"
              rx="1"
              ry="1"
              style={{ fill: "#fff" }}
            ></rect>
            <rect
              x="32"
              y="27"
              width="8"
              height="2"
              rx="1"
              ry="1"
              style={{ fill: "#fff" }}
            ></rect>
            <rect
              x="32"
              y="32"
              width="8"
              height="2"
              rx="1"
              ry="1"
              style={{ fill: "#fff" }}
            ></rect>
            <rect
              x="32"
              y="37"
              width="8"
              height="2"
              rx="1"
              ry="1"
              style={{ fill: "#fff" }}
            ></rect>
          </g>
        </svg>
      </div>
    );
  };

  return (
    <div className="nk-upload-item">
      {getFileIcon()}
      <div className="px-2 nk-upload-info">
        <div className="nk-upload-title">
          <span className="title">{file.name}</span>
        </div>
        <div className="nk-upload-size">{formatFileSize(file.size)}</div>
      </div>
      <div className="nk-upload-action">
        <button
          type="button"
          className="btn btn-icon btn-trigger"
          onClick={onRemove}
        >
          <em className="icon ni ni-trash"></em>
        </button>
      </div>
    </div>
  );
};

const UploadModal: React.FC<UploadModalProps> = ({
  show,
  onClose,
  onUpload,
  isUploading = false,
}) => {
  const intl = useIntl();
  const toast = useContextToast();
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const validFiles = acceptedFiles.filter((file) => {
        if (file.size > MAX_FILE_SIZE) {
          toast.show(
            intl.formatMessage({ id: "ERRORS.FILE_TOO_LARGE" }),
            "error",
          );
          return false;
        }
        return true;
      });

      setStagedFiles((prev) => {
        const newFiles = [...prev, ...validFiles];
        if (newFiles.length > MAX_FILES) {
          toast.show(intl.formatMessage({ id: "ERRORS.MAX_FILES" }), "error");
          return prev;
        }
        return newFiles;
      });
    },
    [intl],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    multiple: true,
  });

  const removeFile = (index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (stagedFiles.length > 0) {
      onUpload(stagedFiles);
      setStagedFiles([]);
    }
  };

  const handleClose = () => {
    setStagedFiles([]);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <div className="modal-content">
        <a href="#" className="close" onClick={handleClose}>
          <em className="icon ni ni-cross-sm"></em>
        </a>
        <div className="modal-body modal-body-md">
          <div className="nk-upload-form">
            <h5 className="title mb-3">
              <FormattedMessage id="Add files" />
            </h5>
            <div
              {...getRootProps()}
              className={`upload-zone small bg-lighter dropzone ${isDragActive ? "dz-drag-hover" : ""}`}
              style={{ cursor: "pointer" }}
            >
              <input {...getInputProps()} />
              <div className="dz-message">
                <span className="dz-message-text">
                  <FormattedMessage id="Drag and drop file" />
                </span>
                <span className="dz-message-or">
                  <FormattedMessage id="or" />
                </span>
                <button className="btn btn-primary">
                  <FormattedMessage id="SELECT" />
                </button>
              </div>
            </div>
          </div>

          {stagedFiles.length > 0 && (
            <div className="nk-upload-list">
              <h6 className="title mb-0">
                <FormattedMessage id="Pending to upload" />{" "}
                <span className="text-muted">({stagedFiles.length})</span>
              </h6>
              <div style={{ maxHeight: "30vh", overflowY: "auto" }}>
                {stagedFiles.map((file, index) => (
                  <FilePreview
                    key={`${file.name}-${index}`}
                    file={file}
                    onRemove={() => removeFile(index)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="nk-modal-action justify-end">
            <ul className="btn-toolbar g-4 align-center">
              <li>
                <a href="#" className="link link-primary" onClick={handleClose}>
                  <FormattedMessage id="Cancel" />
                </a>
              </li>
              <li>
                <Button
                  className="btn btn-primary"
                  onClick={handleUpload}
                  disabled={stagedFiles.length === 0 || isUploading}
                >
                  {isUploading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <FormattedMessage id="Uploading..." />
                    </>
                  ) : (
                    <FormattedMessage id="Upload files" />
                  )}
                </Button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UploadModal;
